import { openHelpChat, openHelpChatSuccess } from '~/actions/help.actions';
import { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHelpStatus } from '~/selectors/help.selectors';
import { requestableIsLoading } from '~/utils/fetchable';
import { ZendeskWidgetContext } from './ZendeskWidgetContext';

const widgetSettings = {
  contactOptions: {
    enabled: true,
    contactButton: { '*': 'Get in touch' },
    chatLabelOnline: { '*': 'Live Chat with us' },
    contactFormLabel: { '*': 'Send an email instead' },
  },
  contactForm: {
    suppress: false,
  },
  chat: {
    suppress: false,
  },
  talk: {
    suppress: false,
  },

  webWidget: {
    contactForm: {
      suppress: false,
    },
    chat: {
      departments: { enabled: [] },
    },
    talk: {
      suppress: false,
    },
    launcher: {
      chatLabel: { '*': 'Get Help' },
    },
    position: { horizontal: 'left', vertical: 'bottom' },
  },
};

const tagId = 'ze-snippet';
const widgetUrl =
  'https://static.zdassets.com/ekr/snippet.js?key=c7815384-61b8-4017-8130-5c9000f36c0f';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setWidgetSettings(settings: any) {
  window.zESettings = settings;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createTag(onLoad: any) {
  const tag = document.createElement('script');
  tag.src = widgetUrl;
  tag.id = tagId;

  tag.onload = onLoad;

  return document.body.appendChild(tag);
}

// This is used exclusively by the context provider. You should not import this hook directly.
export function useProviderHelp() {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const helpStatus = useSelector(getHelpStatus);
  const isLoading = requestableIsLoading(helpStatus);

  const openChat = useCallback(() => {
    if (ENV_ID !== 'production') {
      return alert('Not available in dev environments');
    }

    const scriptExists = !!document.getElementById(tagId);

    if (!scriptExists) {
      dispatch(openHelpChat());
      let innerLoading = true;

      setWidgetSettings(widgetSettings);

      return createTag(() => {
        window.zE('webWidget:on', 'open', () => {
          if (innerLoading) {
            dispatch(openHelpChatSuccess());
            innerLoading = false;
          }

          setIsOpen(true);
          window.zE('webWidget', 'show');
        });

        window.zE('webWidget:on', 'close', () => {
          setIsOpen(false);
          window.zE('webWidget', 'hide');
        });

        window.zE('webWidget', 'open');
      });
    }

    return window.zE('webWidget', 'toggle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return { openChat, isLoading, isOpen };
}

// This is what you should use if you want to open the help chat.
export default function useHelp() {
  const { openChat, isLoading, isOpen } = useContext(ZendeskWidgetContext);

  return {
    openChat,
    isLoading,
    isOpen,
  };
}
